import useAnalytics from '../../../hooks/useAnalytics';
import {
  React, useState, useEffect,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useProject } from '../../../config/ProjectContext';
import GraphqlService from '../../../service/graphqlService';
import SimpleAlert from '../../../components/utils/SimpleAlert';
// import useIsDevicePermissionAllowed from '../../../hooks/useIsDevicePermissionAllowed';
import JSONBuilderFilters, { valueTypes } from '../../../components/utils/JSONBuilderFilters';
import BrandObjectTable from '../MachineTelemetry/BrandObjectTable';
import { validate, calculateEndDateTimeIso, calculateStartDateTimeIso } from '../../../utils/dateUtils';

const jsonColumns = [
  {
    id: 'created_on_local', label: 'Device Timestamp (Local)', valueType: valueTypes.DATE, values: [],
  },
  {
    id: 'created_on_utc', label: 'Device Timestamp (UTC)', valueType: valueTypes.DATE, values: [],
  },
  {
    id: 'country_code', label: 'Market', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'event_code', label: 'Event', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'extra', label: 'Extra', valueType: valueTypes.STRING, values: [],
  },
  {
    id: 'processed_on_utc', label: 'Platform Timestamp UTC', valueType: valueTypes.DATE, values: [],
  },
];

export default function BrandEvents({
  deviceSerial, useProcessedTimestamp = false, onNoResults, triggerEventsSearch,
}) {
  const { setAnalytics } = useAnalytics();
  const MAX_DAYS_BETWEEN_DATES = 31;
  const baseParams = { add_dict: true };

  const { project } = useProject();

  const [totalCount, setTotalCount] = useState(0);
  const [reducedPage, setReducedPage] = useState(0);
  const [rowsPerPageConsumption] = useState(10);
  const [reducedResponse, setReducedResponse] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertHeader, setAlertHeader] = useState('');
  const [alertText, setAlertText] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(moment().add(-7, 'days'));
  const [selectedStartTime, setSelectedStartTime] = useState(moment().format('12:00'));
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [selectedEndTime, setSelectedEndTime] = useState(moment().format('23:59'));

  const [filterError, setFilterError] = useState('');

  // const { isAllowed: isDeviceAllowed } = useIsDevicePermissionAllowed(deviceSerial);

  function openAlert(header, text) {
    setAlertHeader(header);
    setAlertText(text);
    setAlertOpen(true);
  }

  const addDeviceSerialAsFilter = (filters) => {
    try {
      const filtersParsed = JSON.parse(filters || '{ "filter": [] }');
      filtersParsed.filter.push({ column: 'device_serial', operator: 'EQ', value: deviceSerial });
      return JSON.stringify(filtersParsed);
    } catch (err) {
      openAlert('Error addDeviceSerialAsFilter', err.message);
      return '';
    }
  };

  async function searchObject(newPage) {
    // if (!isDeviceAllowed) {
    //   openAlert('Error', 'You do not have permission to access this device');
    //   return [];
    // }
    const startDate = calculateStartDateTimeIso(selectedStartDate, selectedStartTime);
    const endDate = calculateEndDateTimeIso(selectedEndDate, selectedEndTime);
    const consumptionResponse = await GraphqlService.getTelemetryObject(
      project.code, 'events', JSON.stringify(baseParams), addDeviceSerialAsFilter(selectedFilters), startDate, endDate, rowsPerPageConsumption, (newPage || 0) * rowsPerPageConsumption || 0, MAX_DAYS_BETWEEN_DATES, useProcessedTimestamp,
    );
    if (consumptionResponse) {
      if (totalCount < 1 || !newPage) {
        setTotalCount(consumptionResponse.pagination.totalRows);
      }
      return JSON.parse(consumptionResponse.data);
    }
    return [];
  }

  async function search(newPage) {
    try {
      setSubmitting(true);
      setLoading(true);
      let creationResponse = [];

      setReducedPage(newPage || 0);
      creationResponse = await searchObject(newPage);
      setReducedResponse(creationResponse);

      setSubmitting(false);
      setLoading(false);

      if (!useProcessedTimestamp && creationResponse.length === 0 && onNoResults) {
        const startDate = calculateStartDateTimeIso(selectedStartDate, selectedStartTime);
        const endDate = calculateEndDateTimeIso(selectedEndDate, selectedEndTime);
        onNoResults(startDate, endDate);
      }

      return creationResponse;
    } catch (error) {
      setSubmitting(false);
      setLoading(false);
      openAlert('Error', error.message);
      return null;
    }
  }

  const onSearch = (event) => {
    event.preventDefault();
    if (!validate(selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime, MAX_DAYS_BETWEEN_DATES)) return;
    setAnalytics({
      event: 'button_clicked',
      extraParams: {
        name: 'device_management-overview-telemetry-search-events'
      },
    });
    search(0);
  };

  useEffect(() => {
    if (validate(selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime, MAX_DAYS_BETWEEN_DATES)) {
      search(0);
    }
  }, [triggerEventsSearch]);

  function transformData(data) {
    return data?.map((item) => {
      // Get all extra properties and concatenate them with semicolon
      const extraValues = [];
      if (item.extra && typeof item.extra === 'object') {
        Object.values(item.extra).forEach(value => {
          if (value !== null && value !== undefined && value !== '') {
            extraValues.push(String(value));
          }
        });
      }
      // Also check for flat extra properties
      Object.keys(item).forEach(key => {
        if (key.startsWith('extra.') && item[key] !== null && item[key] !== undefined && item[key] !== '') {
          extraValues.push(String(item[key]));
        }
      });
      const extraConcatenated = extraValues.join(' ; ');

      const eventDescription = item.dict_event?.description || '';
      const errorType = item.dict_event?.extra?.error_type || '';

      // Always show code, even if name is missing
      const eventCode = item.dict_event?.code || item.event_code || '';
      const eventName = item.dict_event?.name || '';
      let event;

      if (eventCode !== '-') {
        if (eventName) {
          event = `${eventCode} - ${eventName}`;
        } else {
          event = eventCode;
        }

        if (eventDescription || errorType) {
          const details = [eventDescription, errorType].filter(Boolean).join('; ');
          event += ` (${details})`;
        }
      } else {
        event = '-';
      }

      return {
        'Device Timestamp (Local)': item.created_on_local || '-',
        'Device Timestamp (UTC)': item.created_on_utc || '-',
        ...(item.module_serial ? { 'Module Serial': item.module_serial } : {}),
        Market: item.country_code || '-',
        Event: event,
        Extra: extraConcatenated || '-',
        'Platform Timestamp (UTC)': item.processed_on_utc || '-',
      };
    });
  }

  const transformedData = transformData(reducedResponse || []);

  return (
    <div className="space-y-4">
      <SimpleAlert open={alertOpen} setOpen={setAlertOpen} header={alertHeader} body={alertText} />

      <div className="w-full">
        <JSONBuilderFilters
          id="DeviceEventsOverview"
          setSelectedFilters={setSelectedFilters}
          submitting={submitting}
          selectedStartDate={selectedStartDate}
          setSelectedStartDate={setSelectedStartDate}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
          selectedEndDate={selectedEndDate}
          setSelectedEndDate={setSelectedEndDate}
          selectedEndTime={selectedEndTime}
          setSelectedEndTime={setSelectedEndTime}
          filtersVisible
          deviceSerialVisible={false}
          jsonColumns={jsonColumns}
          onSearch={onSearch}
          setFilterError={setFilterError}
          filterError={filterError}
        />
      </div>

      {loading ? (
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 animate-pulse"></div>
        </div>
      ) : (
        <div className="overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          <BrandObjectTable
            search={(newPage) => search(newPage)}
            dataList={transformedData}
            totalCount={totalCount}
            filters={deviceSerial ? addDeviceSerialAsFilter(selectedFilters) : selectedFilters}
            loading={loading}
            dateIni={calculateStartDateTimeIso(selectedStartDate, selectedStartTime)}
            dateEnd={calculateEndDateTimeIso(selectedEndDate, selectedEndTime)}
            rangeDates={MAX_DAYS_BETWEEN_DATES}
            page={reducedPage}
            setPage={setReducedPage}
            rowsPerPage={rowsPerPageConsumption}
            object="events"
            params={useProcessedTimestamp ? { ...baseParams, use_processed_timestamp: true } : baseParams}
            id="DeviceEvents"
            setFilterError={setFilterError}
            filterError={filterError}
            disableOrderingHeader
          />
        </div>
      )}
    </div>
  );
}

BrandEvents.propTypes = {
  deviceSerial: PropTypes.string.isRequired,
  useProcessedTimestamp: PropTypes.bool,
  onNoResults: PropTypes.func,
  triggerEventsSearch: PropTypes.any,
};

BrandEvents.defaultProps = {
  useProcessedTimestamp: false,
  onNoResults: null,
  triggerEventsSearch: null,
};
